import { gameLabel } from "@loot-os/fulfillment-core/catalog/game";
import {
  AVAILABILITY_STRATEGIES,
  PRICING_SOURCES,
  type PricingSource,
} from "@loot-os/fulfillment-core/catalog/item";
import { printingsForCardGame, TCG_CARD_GAMES } from "@loot-os/fulfillment-core/catalog/tcg-import";
import { Button } from "@loot-os/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@loot-os/ui/components/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@loot-os/ui/components/collapsible";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldLabel,
} from "@loot-os/ui/components/field";
import { Input } from "@loot-os/ui/components/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import { toast } from "@loot-os/ui/components/sonner";
import { Switch } from "@loot-os/ui/components/switch";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import { useState } from "react";

import { CatalogItemBulkImport } from "../../components/CatalogItemBulkImport";
import { RasterImageField } from "../../components/StorefrontFields";
import {
  CATALOG_ITEM_KIND_LABELS,
  CATALOG_ITEM_KINDS,
  CATALOG_ITEM_STATUSES,
  type CatalogItemCreateDraft,
  type CatalogItemFieldErrors,
  type CatalogItemKind,
  CIRCULATION_OVERRIDES,
  draftForKind,
  emptyCatalogItemDraft,
  isMember,
  suggestedValueDollars,
  toCreateCatalogItemInput,
} from "../../lib/catalogItemCreate";
import { uploadTenantAssetFile } from "../../lib/tenantAssets";
import { useTRPC } from "../../lib/trpc";

const NONE = "none";

const PRICING_LABELS: Record<PricingSource, string> = {
  TCGPLAYER: "TCGplayer",
  MANUAL: "Manual",
  STOCKX: "StockX",
  FANATICS: "Fanatics",
  SHOPIFY: "Shopify",
  OTHER: "Other",
};
const STRATEGY_LABELS = { DYNAMIC: "Dynamic", FIXED: "Fixed" };
const STATUS_LABELS = {
  STANDARD: "Standard",
  PRE_RELEASE: "Pre-release",
  ARCHIVED: "Archived",
};
const CIRCULATION_LABELS = {
  [NONE]: "Policy default",
  OPEN: "Open",
  OFFICIAL_ONLY: "Official boxes only",
};
const CARD_GAME_LABELS = Object.fromEntries(
  TCG_CARD_GAMES.map((game) => [game, gameLabel(game) ?? game]),
);

/** Create a catalog item. The kind decides which fields are asked; the rest is derived. */
export function CatalogItemsPage() {
  const trpc = useTRPC();
  const [draft, setDraft] = useState(emptyCatalogItemDraft);
  const [errors, setErrors] = useState<CatalogItemFieldErrors>({});
  const [attempted, setAttempted] = useState(false);
  const [additionalOpen, setAdditionalOpen] = useState(false);
  const [valueEdited, setValueEdited] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [created, setCreated] = useState<{ id: string; name: string } | null>(null);
  const manual = draft.kind !== "SINGLE";
  const name = draft.name.trim();
  const productId = draft.sourceId.trim();
  const printing = draft.printing.trim();
  const duplicateEnabled = manual ? name.length > 0 : productId.length > 0 && printing.length > 0;

  const duplicates = useQuery({
    ...trpc.platform.fulfillment.items.likelyDuplicates.queryOptions(
      manual ? { name } : { productId, printing },
    ),
    enabled: duplicateEnabled,
  });
  const matches = duplicateEnabled ? (duplicates.data ?? []) : [];
  const tcgDuplicate = !manual && matches.length > 0;
  const checkingTcgDuplicate = !manual && duplicateEnabled && duplicates.isFetching;

  const create = useMutation(
    trpc.platform.fulfillment.items.create.mutationOptions({
      onSuccess: (item) => {
        toast.success(`Created ${item.name}.`);
        setCreated({ id: item.id, name: item.name });
        setDraft(emptyCatalogItemDraft());
        setErrors({});
        setAttempted(false);
        setAdditionalOpen(false);
        setValueEdited(false);
        setImageUploading(false);
      },
    }),
  );

  const update = <K extends keyof CatalogItemCreateDraft>(
    key: K,
    value: CatalogItemCreateDraft[K],
  ) => {
    setDraft((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "cardGame") next.printing = "";
      return next;
    });
    setErrors((prev) => {
      if (prev[key] === undefined && (key !== "cardGame" || prev.printing === undefined)) {
        return prev;
      }
      const next = { ...prev };
      delete next[key];
      if (key === "cardGame") delete next.printing;
      return next;
    });
  };

  const chooseKind = (kind: CatalogItemKind) => {
    if (kind === draft.kind) return;
    setDraft(draftForKind(kind));
    setErrors({});
    setAttempted(false);
    setValueEdited(false);
    setImageUploading(false);
  };

  const onMarketPrice = (raw: string) => {
    setDraft((prev) => {
      const next = { ...prev, marketPriceDollars: raw };
      if (!valueEdited) next.valueDollars = suggestedValueDollars(raw) ?? "";
      return next;
    });
    setErrors((prev) => {
      if (prev.marketPriceDollars === undefined && prev.valueDollars === undefined) return prev;
      const next = { ...prev };
      delete next.marketPriceDollars;
      if (!valueEdited) delete next.valueDollars;
      return next;
    });
  };

  const submit = () => {
    if (create.isPending || tcgDuplicate || checkingTcgDuplicate) return;
    setAttempted(true);
    const parsed = toCreateCatalogItemInput(draft);
    if (!parsed.ok) {
      setErrors(parsed.errors);
      if (
        parsed.errors.compareAtValueDollars ||
        parsed.errors.compareAtLabelOverride ||
        (draft.kind === "SINGLE" && parsed.errors.quantity)
      ) {
        setAdditionalOpen(true);
      }
      return;
    }
    setErrors({});
    create.mutate(parsed.input);
  };

  const game = isMember(TCG_CARD_GAMES, draft.cardGame) ? draft.cardGame : null;
  const printingLabels = game
    ? Object.fromEntries(printingsForCardGame(game).map((printing) => [printing, printing]))
    : {};
  const marketPriceReady = suggestedValueDollars(draft.marketPriceDollars) !== null;

  return (
    <div className="flex flex-col gap-6">
      <form
        className="flex flex-col gap-6"
        autoComplete="off"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Catalog items</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Create one item, or import a CSV of TCGplayer cards below. Choose the kind first.
            Amounts are dollars.
          </p>
        </div>

        {created ? (
          <p className="rounded-md border bg-muted/40 px-3 py-2 text-sm">
            Created <span className="font-medium">{created.name}</span>. Id{" "}
            <span className="font-mono text-xs">{created.id}</span>
          </p>
        ) : null}

        {attempted && Object.keys(errors).length > 0 ? (
          <p className="text-sm text-destructive" role="alert">
            Fix the fields below before creating the item.
          </p>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle>{CATALOG_ITEM_KIND_LABELS[draft.kind]}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <EnumField
              id="catalog-kind"
              className="sm:col-span-2"
              label="Kind"
              value={draft.kind}
              labels={CATALOG_ITEM_KIND_LABELS}
              onChange={(value) => {
                if (isMember(CATALOG_ITEM_KINDS, value)) chooseKind(value);
              }}
            />
            <TextField
              id="catalog-name"
              className="sm:col-span-2"
              label="Full name"
              description="The name staff and tenants see."
              error={errors.name}
              value={draft.name}
              onChange={(value) => update("name", value)}
              required
            />
            {manual && matches.length > 0 ? (
              <p className="break-words text-sm text-amber-600 sm:col-span-2">
                An item already has this name: <DuplicateNames items={matches} />
              </p>
            ) : null}
            {manual ? (
              <>
                <TextField
                  id="catalog-collection"
                  className="sm:col-span-2"
                  label="Collection name"
                  description="The set name, or other collection name."
                  error={errors.collectionName}
                  value={draft.collectionName}
                  onChange={(value) => update("collectionName", value)}
                  required
                />
                <TextField
                  id="catalog-market-price"
                  label="Market price"
                  description="Purchase price or Market price. Value is derived from this."
                  error={errors.marketPriceDollars}
                  value={draft.marketPriceDollars}
                  onChange={onMarketPrice}
                  inputMode="decimal"
                  placeholder="0.00"
                  required
                />
                {marketPriceReady ? (
                  <TextField
                    id="catalog-value"
                    label="Value"
                    description="Starts at 1.25 times the market price, rounded to the nearest cent."
                    error={errors.valueDollars}
                    value={draft.valueDollars}
                    onChange={(value) => {
                      setValueEdited(true);
                      update("valueDollars", value);
                    }}
                    inputMode="decimal"
                    placeholder="0.00"
                    required
                  />
                ) : null}
              </>
            ) : (
              <TextField
                id="catalog-source-id"
                label="Source id"
                description="The TCGplayer product id."
                error={errors.sourceId}
                value={draft.sourceId}
                onChange={(value) => update("sourceId", value)}
                spellCheck={false}
                required
              />
            )}
            <EnumField
              id="catalog-card-game"
              label="Card game"
              description="Saved as the item's game and tag."
              error={errors.cardGame}
              value={draft.cardGame}
              labels={CARD_GAME_LABELS}
              placeholder="Choose a game"
              onChange={(value) => update("cardGame", value)}
            />
            <EnumField
              id="catalog-printing"
              label="Card printing"
              error={errors.printing}
              value={draft.printing}
              labels={printingLabels}
              placeholder={
                game ? (manual ? "None" : "Choose a printing") : "Choose a card game first"
              }
              disabled={!game}
              onChange={(value) => update("printing", value)}
            />
            {tcgDuplicate ? (
              <p className="break-words text-sm text-destructive sm:col-span-2" role="alert">
                A catalog item with this TCGplayer id and {printing} printing already exists:{" "}
                <DuplicateNames items={matches} />
              </p>
            ) : null}
            {manual ? (
              <div className="sm:col-span-2">
                <CatalogImageField
                  value={draft.image}
                  uploading={imageUploading}
                  disabled={create.isPending}
                  onChange={(value) => update("image", value)}
                  onUploadingChange={setImageUploading}
                />
              </div>
            ) : null}
            <EnumField
              id="catalog-pricing-source"
              label="Pricing source"
              value={draft.pricingSource}
              labels={PRICING_LABELS}
              onChange={(value) => {
                if (isMember(PRICING_SOURCES, value)) update("pricingSource", value);
              }}
            />
            <EnumField
              id="catalog-availability"
              label="Availability"
              description={
                manual
                  ? "Starts at Fixed: a box can contain this only while a unit is on hand."
                  : "Starts at Dynamic: a unit can be bought when someone needs one."
              }
              value={draft.availabilityStrategy}
              labels={STRATEGY_LABELS}
              onChange={(value) => {
                if (isMember(AVAILABILITY_STRATEGIES, value)) update("availabilityStrategy", value);
              }}
            />
            <EnumField
              id="catalog-status"
              label="Item status"
              description={
                manual
                  ? "Default is Standard. Pre-release holds it for a box that has not launched."
                  : "Default is Pre-release, held for a box that has not launched."
              }
              value={draft.status}
              labels={STATUS_LABELS}
              onChange={(value) => {
                if (isMember(CATALOG_ITEM_STATUSES, value)) update("status", value);
              }}
            />
            <EnumField
              id="catalog-circulation"
              label="Circulation"
              description="Who else can use this. A pre-release item stays official-only until it is standard."
              value={draft.overrideCirculation || NONE}
              labels={CIRCULATION_LABELS}
              onChange={(value) => {
                update("overrideCirculation", isMember(CIRCULATION_OVERRIDES, value) ? value : "");
              }}
            />
            {manual ? (
              <AlreadyOnHand
                draft={draft}
                error={errors.quantity}
                onCheckedChange={(checked) => update("alreadyProcured", checked)}
                onQuantityChange={(value) => update("quantity", value)}
              />
            ) : null}
          </CardContent>
        </Card>

        <Collapsible open={additionalOpen} onOpenChange={setAdditionalOpen}>
          <CollapsibleTrigger className="flex items-center gap-1.5 text-sm font-medium">
            <ChevronRight className="size-4 transition-transform in-data-[panel-open]:rotate-90" />
            Additional options
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                id="catalog-compare-at"
                label="Compare-at value"
                description="A higher struck-through number, in dollars. Must be greater than value."
                error={errors.compareAtValueDollars}
                value={draft.compareAtValueDollars}
                onChange={(value) => update("compareAtValueDollars", value)}
                inputMode="decimal"
                placeholder="0.00"
              />
              <TextField
                id="catalog-compare-at-label"
                label="Compare-at label"
                description="Optional label beside that number. At most 40 characters."
                error={errors.compareAtLabelOverride}
                value={draft.compareAtLabelOverride}
                onChange={(value) => update("compareAtLabelOverride", value)}
              />
              {manual ? null : (
                <AlreadyOnHand
                  draft={draft}
                  error={errors.quantity}
                  onCheckedChange={(checked) => update("alreadyProcured", checked)}
                  onQuantityChange={(value) => update("quantity", value)}
                />
              )}
              <TextField
                id="catalog-tenant"
                className="sm:col-span-2"
                label="Tenant"
                description="Blank is the shared catalog. A tenant id binds the item to that tenant."
                value={draft.tenantId}
                onChange={(value) => update("tenantId", value)}
                spellCheck={false}
                inputClassName="font-mono"
              />
            </div>
          </CollapsibleContent>
        </Collapsible>

        <div>
          <Button
            type="submit"
            disabled={create.isPending || imageUploading || tcgDuplicate || checkingTcgDuplicate}
          >
            {checkingTcgDuplicate ? "Checking…" : "Create catalog item"}
          </Button>
        </div>
      </form>
      <CatalogItemBulkImport />
    </div>
  );
}

function DuplicateNames({ items }: { items: readonly { id: string; name: string }[] }) {
  return items.map((item, index) => (
    <span key={item.id}>
      {index > 0 ? ", " : null}
      <Link
        to="/fulfillment/items/$itemId"
        params={{ itemId: item.id }}
        className="underline underline-offset-2"
      >
        {item.name}
      </Link>
    </span>
  ));
}

function CatalogImageField({
  value,
  uploading,
  disabled,
  onChange,
  onUploadingChange,
}: {
  value: string;
  uploading: boolean;
  disabled: boolean;
  onChange: (value: string) => void;
  onUploadingChange: (uploading: boolean) => void;
}) {
  const trpc = useTRPC();
  const available = useQuery(trpc.platform.fulfillment.items.imageUploadsAvailable.queryOptions());
  const startUpload = useMutation(
    trpc.platform.fulfillment.items.startImageUpload.mutationOptions(),
  );
  const finishUpload = useMutation(
    trpc.platform.fulfillment.items.finishImageUpload.mutationOptions(),
  );

  const upload = async (file: File) => {
    onUploadingChange(true);
    try {
      const { publicUrl } = await uploadTenantAssetFile(
        file,
        (input) => startUpload.mutateAsync(input),
        (uploadKey) => finishUpload.mutateAsync({ uploadKey }),
      );
      onChange(publicUrl);
      toast.success("Image uploaded.");
    } finally {
      onUploadingChange(false);
    }
  };

  return (
    <RasterImageField
      id="catalog-image"
      label="Image"
      url={value}
      onUrlChange={onChange}
      onFile={upload}
      uploading={uploading}
      disabled={disabled}
      uploadsAvailable={available.data !== false}
      previewClassName="size-full object-contain"
      help="Optional. Upload a PNG, JPEG, WEBP or AVIF, or paste a URL. An upload is stored on this stage's image CDN and shown on the item."
    />
  );
}

function AlreadyOnHand({
  draft,
  error,
  onCheckedChange,
  onQuantityChange,
}: {
  draft: CatalogItemCreateDraft;
  error?: string;
  onCheckedChange: (checked: boolean) => void;
  onQuantityChange: (value: string) => void;
}) {
  return (
    <>
      <ToggleField
        id="catalog-already-procured"
        className="sm:col-span-2"
        label="Already on hand"
        description="Turn this on when the units are already in the facility, then enter how many."
        checked={draft.alreadyProcured}
        onCheckedChange={onCheckedChange}
      />
      {draft.alreadyProcured ? (
        <TextField
          id="catalog-quantity"
          label="Quantity"
          description="How many units are in the facility."
          error={error}
          value={draft.quantity}
          onChange={onQuantityChange}
          inputMode="numeric"
          required
        />
      ) : null}
    </>
  );
}

function TextField({
  id,
  label,
  description,
  error,
  value,
  onChange,
  className,
  inputClassName,
  required,
  spellCheck,
  inputMode,
  placeholder,
}: {
  id: string;
  label: string;
  description: string;
  error?: string;
  value: string;
  onChange: (value: string) => void;
  className?: string;
  inputClassName?: string;
  required?: boolean;
  spellCheck?: boolean;
  inputMode?: "decimal" | "numeric";
  placeholder?: string;
}) {
  return (
    <Field className={className} data-invalid={error ? true : undefined}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <FieldDescription>{description}</FieldDescription>
      <Input
        id={id}
        className={inputClassName}
        value={value}
        required={required}
        spellCheck={spellCheck}
        inputMode={inputMode}
        placeholder={placeholder}
        aria-invalid={error ? true : undefined}
        onChange={(event) => onChange(event.target.value)}
      />
      <FieldError>{error}</FieldError>
    </Field>
  );
}

function EnumField({
  id,
  label,
  description,
  error,
  value,
  labels,
  onChange,
  className,
  placeholder,
  disabled,
}: {
  id: string;
  label: string;
  description?: string;
  error?: string;
  value: string;
  labels: Record<string, string>;
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <Field className={className} data-invalid={error ? true : undefined}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      {description ? <FieldDescription>{description}</FieldDescription> : null}
      <Select
        items={labels}
        value={value || null}
        disabled={disabled}
        onValueChange={(next) => next && onChange(next)}
      >
        <SelectTrigger id={id} className="w-full">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(labels).map(([option, optionLabel]) => (
            <SelectItem key={option} value={option}>
              {optionLabel}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <FieldError>{error}</FieldError>
    </Field>
  );
}

function ToggleField({
  id,
  label,
  description,
  checked,
  onCheckedChange,
  className,
}: {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  className?: string;
}) {
  return (
    <Field orientation="horizontal" className={className}>
      <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} />
      <FieldContent>
        <FieldLabel htmlFor={id}>{label}</FieldLabel>
        <FieldDescription>{description}</FieldDescription>
      </FieldContent>
    </Field>
  );
}
